/** Explore data (redesign phase 6, the Compare board; workspace v5): up to
 *  eight sensors from any stations, every stored point. Axes group on
 *  `axis_group`, never on unit (HANDBOOK, query-layer rule 3): a Lajanuri
 *  water depth and a Zhinvali piezometer level are both metres and must not
 *  share an axis. One chart carries two axis groups, an axis each side;
 *  picks spanning more are drawn one chart per axis group, stacked over the
 *  same window (`chartPanels`), so the number of quantities no longer stops
 *  the owner analysing several sensors at once. */
import type { CompareChannel, Series } from '../api/types'
import { SERIES_COLOUR } from '../components/TimeSeriesChart'
import { STATION_TZ } from './time'

/** Still eight with the axis limit gone: the chart has eight approved series
 *  colours (DESIGN_GUIDELINE section 7, D1). */
export const MAX_SERIES = 8
/** Axis groups one chart carries, one Y axis each side. */
export const MAX_AXES = 2

export function channelId(c: Pick<CompareChannel, 'station' | 'parameter'>): string {
  return `${c.station}|${c.parameter}`
}

/** What the explorer needs of a pickable sensor: a stored channel
 *  (CompareChannel) or any analysis source (lib/analysisSources). */
export type Pickable = Pick<CompareChannel,
  'station' | 'parameter' | 'station_name' | 'display_name' | 'unit' | 'axis_group'>

/** What an axis is called: its unit, with the group's own name beside it
 *  when two groups on the chart share a unit. */
export function axisLabels(picked: Pick<CompareChannel, 'axis_group' | 'unit'>[]): Map<string, string> {
  const groups = new Map<string, string>()
  for (const c of picked) if (!groups.has(c.axis_group)) groups.set(c.axis_group, c.unit)
  const units = [...groups.values()]
  return new Map([...groups].map(([group, unit]) => {
    const shared = units.filter((u) => u === unit).length > 1 || !unit
    return [group, shared ? `${unit ? `${unit}, ` : ''}${group}` : unit]
  }))
}

export type PickResult<T = CompareChannel> = { ok: true; picked: T[] } | { ok: false; message: string }

export function seriesName(c: Pick<CompareChannel, 'station_name' | 'display_name'>): string {
  return `${c.station_name} · ${c.display_name}`
}

/** One chart's rule: tick a channel, or the sentence saying why not, past
 *  MAX_SERIES or past MAX_AXES axis groups. Explore data no longer refuses
 *  a third quantity, it stacks a chart for it (`chartPanels`) and limits
 *  only the count (lib/sourceGroups pickWithin); the saved-comparison
 *  recheck in lib/savedViews still applies this. */
export function tryPick<T extends Pickable>(picked: T[], add: T): PickResult<T> {
  if (picked.some((c) => channelId(c) === channelId(add))) return { ok: true, picked }
  if (picked.length >= MAX_SERIES) {
    return { ok: false, message: `Up to ${MAX_SERIES} sensors at once. Untick one first.` }
  }
  const groups = new Set(picked.map((c) => c.axis_group))
  if (!groups.has(add.axis_group) && groups.size >= MAX_AXES) {
    const labels = [...axisLabels(picked).values()].join(' and ')
    return { ok: false, message: `Up to ${MAX_AXES} axes at once (${labels}). Untick one to add `
      + `${seriesName(add)}.` }
  }
  return { ok: true, picked: [...picked, add] }
}

export function colourOf(index: number): string {
  return SERIES_COLOUR[index % SERIES_COLOUR.length]!
}

/** The picks in the address: `s=station|parameter,calculated|id,...`, each
 *  an analysis source id (lib/analysisSources), a stored channel's being
 *  its station and parameter. */
export function picksFromParam(param: string | null): string[] {
  if (!param) return []
  return [...new Set(param.split(',').filter((p) => /^[^|]+\|[^|]+$/.test(p)))].slice(0, MAX_SERIES)
}

/** The stored-channel pairs when every pick is a stored channel, else null.
 *  A raw-only selection is read exactly as Explore sensors always read it,
 *  one /api/series request of exact pairs. One with a calculated sensor, or
 *  any other type, goes through fetchAnalysisSeries (lib/analysisSources),
 *  which reads its raw inputs in that same single request and derives the
 *  rest beside them, never in place of a stored reading. */
export function rawPairs(picked: { station: string; parameter: string; type: string }[]):
    [string, string][] | null {
  if (picked.some((p) => p.type !== 'raw')) return null
  return picked.map((p) => [p.station, p.parameter])
}

// ------------------------------------------------------------ the charts

/** One chart's share of the picks. */
export interface ChartPanel<T> { key: string; groups: string[]; items: T[] }

/** The charts the picks are drawn on. Up to MAX_AXES axis groups share one
 *  chart, an axis each side, as before. Past that, one chart per axis group
 *  in the order its first sensor was picked, each grouping its Y axis on
 *  axis_group as the single chart does, never on unit (HANDBOOK rule 3):
 *  stacking two metre groups on one axis would flatten the smaller. Every
 *  chart reads the page's one window and its one /api/series request, so
 *  under truncation all of them stop at the same instant (rule 5). */
export function chartPanels<T extends Pick<Series, 'axis_group'>>(items: T[],
    maxAxes = MAX_AXES): ChartPanel<T>[] {
  const groups = [...new Set(items.map((item) => item.axis_group))]
  if (groups.length <= maxAxes) return items.length ? [{ key: 'all', groups, items }] : []
  return groups.map((group) => ({ key: `axis:${group}`, groups: [group],
    items: items.filter((item) => item.axis_group === group) }))
}

/** Each pick's place in the whole selection, which is its colour
 *  (TimeSeriesChart seriesStyle) on whichever chart it is drawn, its chip
 *  and its swatch in the list. Counted across the page rather than per
 *  chart: per chart, the first sensor of every stacked chart took the first
 *  colour, and two sensors read as one. MAX_SERIES equals the eight series
 *  colours, so no two picks share one. */
export function colourSlots(picks: string[]): Map<string, number> {
  return new Map(picks.map((id, index) => [id, index]))
}

/** The span every stacked chart draws, epoch ms: the window asked for,
 *  ending where the API stopped when it truncated (`truncated_at`, where
 *  every series is cut). One span for all, so an instant sits at the same
 *  x on each chart and one zoom percentage is one stretch of time. */
export function chartExtent(from: Date, to: Date, truncatedAt?: string | null): [number, number] {
  const cut = truncatedAt ? Date.parse(truncatedAt) : NaN
  const end = Number.isNaN(cut) ? to.getTime() : Math.min(cut, to.getTime())
  return [from.getTime(), Math.max(end, from.getTime())]
}

/** A chart as drawn: its share of the series in the panel's order, each
 *  series' colour slot, and the span its time axis draws. */
export interface DrawnPanel<T> extends ChartPanel<T> {
  series: Series[]
  styles: number[]
  extent: [number, number]
}

/** The panels with their series from the one response, every panel given
 *  the same extent and each series the colour of its sensor's place in the
 *  selection. A panel none of whose sensors came back is left out; a
 *  series is matched to its sensor by `station|parameter`, the analysis
 *  source id (lib/analysisSources). */
export function drawnPanels<T>(panels: ChartPanel<T>[], series: Series[], idOf: (item: T) => string,
                               slots: Map<string, number>, extent: [number, number]): DrawnPanel<T>[] {
  return panels.map((panel) => {
    const own = panel.items.flatMap((item) => series.filter((s) => channelId(s) === idOf(item)))
    return { ...panel, series: own, styles: own.map((s) => slots.get(channelId(s)) ?? 0), extent }
  }).filter((panel) => panel.series.length)
}

/** A stacked chart's heading: the quantity in Title Case words, with its
 *  unit. A derived quantity says so; lib/analysisSources keys a calculated
 *  sensor's axis `calculated:<quantity>:<unit>`, so that a declared unit is
 *  never read as a conversion onto a raw axis. */
export function quantityLabel(group: string, unit: string): string {
  const derived = group.startsWith('calculated:')
  const words = (derived ? group.split(':')[1] ?? '' : group).split(/[_\s]+/).filter(Boolean)
  const name = words.length ? words.map((w) => w[0]!.toUpperCase() + w.slice(1)).join(' ') : 'Quantity'
  return `${name}${derived ? ', derived' : ''}${unit ? ` (${unit})` : ''}`
}

// ------------------------------------------------------------------ CSV

const wallClock = new Intl.DateTimeFormat('en-GB', {
  timeZone: STATION_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
})

/** 2026-10-20 14:30:00, station wall clock, as the station export writes it. */
export function csvTime(iso: string): string {
  const parts = wallClock.formatToParts(new Date(iso))
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? ''
  return `${get('year')}-${get('month')}-${get('day')} ${get('hour')}:${get('minute')}:${get('second')}`
}

/** The two CSV dialects the station export offers (backend
 *  services/export.py `csv_dialect`): for Excel, semicolons and a decimal
 *  comma, since Excel on a Georgian or Russian machine splits on ';' and
 *  reads ',' as the decimal point, so a comma file lands in one column; and
 *  the strict comma file for other tools. */
export type CsvFormat = 'csv-excel' | 'csv'
export const CSV_FORMAT_LABEL: Record<CsvFormat, string> = {
  'csv-excel': 'CSV for Excel (semicolon)',
  'csv': 'CSV (comma, for other tools)',
}
const DIALECT: Record<CsvFormat, { delimiter: string; decimal: string }> = {
  'csv-excel': { delimiter: ';', decimal: ',' },
  'csv': { delimiter: ',', decimal: '.' },
}

/** A stored value at its channel's recorded precision, never losing a digit:
 *  a reading with more decimals than recorded is written whole (the station
 *  export's rule). The decimal separator is the only thing a dialect
 *  changes; the digits stay the stored value's own. */
export function csvValue(value: number | null, precision: number | null, decimal = '.'): string {
  if (value === null) return ''
  let text = String(value)
  if (precision !== null) {
    const fixed = value.toFixed(Math.min(Math.max(Math.trunc(precision), 0), 20))
    if (Number(fixed) === value) text = fixed
  }
  return decimal === '.' ? text : text.replace('.', decimal)
}

function quoter(delimiter: string): (cell: string) => string {
  return (cell) => (cell.includes(delimiter) || /["\n\r]/.test(cell)
    ? `"${cell.replace(/"/g, '""')}"` : cell)
}

/** One row per distinct timestamp, one column per series, ordered by station,
 *  sensor and parameter; an empty cell where a series has no reading at that instant.
 *  Starts with a BOM, as the station export does: Excel on Windows reads a
 *  UTF-8 file without one as Latin-1, and °C or m³ in the header would come
 *  out garbled. A calculated sensor's column is keyed and named as its
 *  analysis source (`calculated|id`, "Calculated sensors · name") and
 *  holds the derived values beside, never in place of, the stored ones. */
export function buildCsv(series: Series[],
                         channels: Map<string, Pick<CompareChannel, 'station_name' | 'display_name' | 'precision'> & { sensor?: string | null }>,
                         format: CsvFormat): string {
  const { delimiter, decimal } = DIALECT[format]
  const quote = quoter(delimiter)
  // Ready to use (Tamar, 2026-11-01): one column per sensor and parameter,
  // ordered station, sensor, parameter, and four rows above the readings
  // saying which: Station, Sensor, Parameter, Unit. The fifth row names
  // each column whole, for a tool that reads one header line after
  // skipping four.
  const columns = series.map((s) => {
    const c = channels.get(channelId({ station: s.station, parameter: s.parameter }))
    // A calculated or manual sensor has no logger or node: its column's
    // Sensor cell is left empty rather than name one.
    const sensor = c?.sensor ?? ''
    const name = c?.display_name ?? s.display_name
    const parameter = name.includes(' · ') ? name.split(' · ').at(-1)!
      : sensor && name.startsWith(`${sensor} `) ? name.slice(sensor.length + 1) : name
    return { s, precision: c?.precision ?? null, station: c?.station_name ?? s.station, sensor, parameter,
             header: `${c ? seriesName(c) : `${s.station} · ${s.display_name}`}${s.unit ? ` (${s.unit})` : ''}` }
  }).sort((a, b) => a.station.localeCompare(b.station) || a.sensor.localeCompare(b.sensor) || a.parameter.localeCompare(b.parameter))
  const rows = new Map<string, (number | null)[]>()
  columns.forEach(({ s }, i) => {
    for (const [ts, value] of s.points) {
      const row = rows.get(ts) ?? Array<number | null>(columns.length).fill(null)
      row[i] = value
      rows.set(ts, row)
    }
  })
  const lines = [
    ['Station', ...columns.map((c) => c.station)],
    ['Sensor', ...columns.map((c) => c.sensor)],
    ['Parameter', ...columns.map((c) => c.parameter)],
    ['Unit', ...columns.map((c) => c.s.unit)],
    [`Time (${STATION_TZ}, UTC+4)`, ...columns.map((c) => c.header)],
  ].map((row) => row.map(quote).join(delimiter))
  for (const ts of [...rows.keys()].sort((a, b) => Date.parse(a) - Date.parse(b))) {
    const row = rows.get(ts)!
    lines.push([csvTime(ts), ...row.map((v, i) => csvValue(v, columns[i]!.precision, decimal))]
      .join(delimiter))
  }
  return `﻿${lines.join('\r\n')}\r\n`
}
